import { useEffect, useRef, useState } from 'react';
import { Wifi, WifiOff } from 'lucide-react';
import { hasValidLicense } from '../lib/licenseToken';
import { isDesktop } from '../lib/platform';

// Heartbeat cadence (useSessionHeartbeat) sets consecutiveFailures, so relying
// on it alone means the banner can lag behind a real disconnection. We pair
// it with the browser's online/offline events for immediate feedback;
// consecutiveFailures still drives the escalation to the harder "can't reach
// servers" state when the browser thinks it's online but our API is
// unreachable.
export default function ConnectivityBanner({ consecutiveFailures }) {
  const [online, setOnline] = useState(() =>
    typeof navigator !== 'undefined' && 'onLine' in navigator ? navigator.onLine : true
  );
  const [showConnected, setShowConnected] = useState(false);
  const prevDown = useRef(false);
  const connectedTimer = useRef(null);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const isDown = !online || consecutiveFailures > 0;
  const licensedDesktop = isDesktop() && hasValidLicense();

  useEffect(() => {
    if (prevDown.current && !isDown) {
      setShowConnected(true);
      if (connectedTimer.current) clearTimeout(connectedTimer.current);
      connectedTimer.current = setTimeout(() => setShowConnected(false), 2000);
    }
    prevDown.current = isDown;
    return () => {
      if (connectedTimer.current) clearTimeout(connectedTimer.current);
    };
  }, [isDown]);

  if (showConnected) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="fixed top-0 inset-x-0 z-50 flex items-center justify-center gap-2 bg-green-500 text-white text-xs font-medium py-1.5 px-4 shadow-sm animate-fade-out"
      >
        <Wifi className="w-3.5 h-3.5" aria-hidden="true" />
        Connected
      </div>
    );
  }

  // Hard failure: browser-offline for a while, or 2+ heartbeat failures.
  // useSessionHeartbeat now beats every ~5 minutes (plus immediate beats on
  // focus/online/visible), so 3 misses would mean 15+ minutes before this
  // escalates \u2014 too slow. 2 misses pairs with the instant navigator.onLine
  // signal (which already covers true offline immediately) for a faster,
  // still-conservative threshold. A licensed desktop install works fully
  // offline (bundled engines, an Ed25519 license token that never phones
  // home) \u2014 tell those users the truth instead of the generic "features
  // may be limited" warning.
  if (!online || consecutiveFailures >= 2) {
    if (licensedDesktop && !online) {
      return (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-0 inset-x-0 z-50 flex items-center justify-center gap-2 bg-slate-600 text-white text-xs font-medium py-1.5 px-4 shadow-sm"
        >
          <WifiOff className="w-3.5 h-3.5" aria-hidden="true" />
          You&rsquo;re offline &mdash; all PDF tools keep working. AI, cloud storage, and eSign need a connection.
        </div>
      );
    }
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="fixed top-0 inset-x-0 z-50 flex items-center justify-center gap-2 bg-amber-600 text-white text-xs font-medium py-1.5 px-4 shadow-sm"
      >
        <WifiOff className="w-3.5 h-3.5" aria-hidden="true" />
        {online
          ? "Can\u2019t reach Scrivar servers. Some features may be limited."
          : "You\u2019re offline. Some features may be limited."}
      </div>
    );
  }

  if (consecutiveFailures >= 1) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="fixed top-0 inset-x-0 z-50 flex items-center justify-center gap-2 bg-amber-500 text-white text-xs font-medium py-1.5 px-4 shadow-sm"
      >
        <WifiOff className="w-3.5 h-3.5" aria-hidden="true" />
        Reconnecting to Scrivar&hellip;
      </div>
    );
  }

  return null;
}
